package com.speedsoftware.nibbler.ui.boards

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.speedsoftware.nibbler.models.BoardColumnDto
import com.speedsoftware.nibbler.models.BoardDto
import com.speedsoftware.nibbler.models.CreateBoardRequest
import com.speedsoftware.nibbler.models.CreateColumnRequest
import com.speedsoftware.nibbler.models.ProjectDto
import com.speedsoftware.nibbler.models.UpdateBoardRequest
import com.speedsoftware.nibbler.models.UpdateColumnRequest
import com.speedsoftware.nibbler.models.UpdateProjectRequest
import com.speedsoftware.nibbler.ui.AppState
import com.speedsoftware.nibbler.ui.components.ProjectImage
import com.speedsoftware.nibbler.ui.theme.NibblerColors
import kotlinx.coroutines.launch

/**
 * The "Settings" tab of a project: project details (name, description, image) and, per board
 * (e.g. Development, Design), its statuses. Boards double as the project's teams.
 */
@Composable
fun ProjectSettingsScreen(
    appState: AppState,
    project: ProjectDto,
    onProjectChanged: () -> Unit
) {
    val scope = rememberCoroutineScope()

    var name by remember(project.id, project.name) { mutableStateOf(project.name) }
    var description by remember(project.id, project.description) { mutableStateOf(project.description) }
    var imageUrl by remember(project.id, project.imageUrl) { mutableStateOf(project.imageUrl ?: "") }
    var detailsStatus by remember { mutableStateOf<String?>(null) }

    var newBoardName by remember { mutableStateOf("") }

    // Status dialogs; a null board means the dialog is closed
    var addStatusBoard by remember { mutableStateOf<BoardDto?>(null) }
    var editStatus by remember { mutableStateOf<Pair<BoardDto, BoardColumnDto>?>(null) }
    var deleteStatus by remember { mutableStateOf<Pair<BoardDto, BoardColumnDto>?>(null) }
    var deleteBoard by remember { mutableStateOf<BoardDto?>(null) }

    Column(
        modifier = Modifier
            .fillMaxSize()
            .background(NibblerColors.Background)
            .verticalScroll(rememberScrollState())
            .padding(16.dp),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        // --- Project details ---
        SettingsCard(title = "Project") {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                ProjectImage(project = project, size = 56.dp)
                Column {
                    Text(project.name, fontWeight = FontWeight.Bold, color = NibblerColors.OnSurface)
                    Text(project.key, fontSize = 12.sp, color = NibblerColors.OnSurfaceVariant)
                }
            }
            OutlinedTextField(
                value = name,
                onValueChange = { name = it },
                label = { Text("Project name") },
                singleLine = true,
                modifier = Modifier.fillMaxWidth()
            )
            OutlinedTextField(
                value = description,
                onValueChange = { description = it },
                label = { Text("Description") },
                modifier = Modifier.fillMaxWidth()
            )
            OutlinedTextField(
                value = imageUrl,
                onValueChange = { imageUrl = it },
                label = { Text("Project image URL (https://...)") },
                singleLine = true,
                modifier = Modifier.fillMaxWidth()
            )
            Text(
                "The image server must allow cross-origin requests, otherwise the project key is shown instead.",
                fontSize = 11.sp,
                color = NibblerColors.OnSurfaceVariant
            )
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                Button(
                    enabled = name.isNotBlank(),
                    onClick = {
                        scope.launch {
                            appState.apiClient.updateProject(
                                project.id,
                                UpdateProjectRequest(name = name.trim(), description = description, imageUrl = imageUrl.trim())
                            ).onSuccess {
                                detailsStatus = "Saved."
                                onProjectChanged()
                            }.onFailure {
                                detailsStatus = "Could not save. Check that the image URL starts with http:// or https://."
                            }
                        }
                    },
                    shape = RoundedCornerShape(8.dp),
                    colors = ButtonDefaults.buttonColors(containerColor = NibblerColors.Primary)
                ) { Text("Save project") }
                detailsStatus?.let { Text(it, fontSize = 12.sp, color = NibblerColors.OnSurfaceVariant) }
            }
        }

        // --- Boards and their statuses ---
        Text(
            "Boards & statuses",
            fontWeight = FontWeight.Bold,
            fontSize = 16.sp,
            color = NibblerColors.OnSurface
        )
        Text(
            "Every board (for example a development or design team) has its own set of statuses.",
            fontSize = 12.sp,
            color = NibblerColors.OnSurfaceVariant
        )

        project.boards.forEach { board ->
            BoardSettingsCard(
                board = board,
                onRename = { newName ->
                    scope.launch {
                        appState.apiClient.updateBoard(project.id, board.id, UpdateBoardRequest(name = newName))
                        onProjectChanged()
                    }
                },
                onDeleteBoard = { deleteBoard = board },
                onAddStatus = { addStatusBoard = board },
                onEditStatus = { editStatus = board to it },
                onMoveStatus = { column, newPosition ->
                    scope.launch {
                        appState.apiClient.updateColumn(project.id, board.id, column.id, UpdateColumnRequest(position = newPosition))
                        onProjectChanged()
                    }
                },
                onDeleteStatus = { deleteStatus = board to it }
            )
        }

        SettingsCard(title = "Add a board") {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                OutlinedTextField(
                    value = newBoardName,
                    onValueChange = { newBoardName = it },
                    label = { Text("Board name (e.g. Design)") },
                    singleLine = true,
                    modifier = Modifier.weight(1f)
                )
                Button(
                    enabled = newBoardName.isNotBlank(),
                    onClick = {
                        val boardName = newBoardName.trim()
                        newBoardName = ""
                        scope.launch {
                            appState.apiClient.createBoard(
                                project.id,
                                CreateBoardRequest(name = boardName, description = "$boardName board", position = project.boards.size)
                            )
                            onProjectChanged()
                        }
                    },
                    shape = RoundedCornerShape(8.dp),
                    colors = ButtonDefaults.buttonColors(containerColor = NibblerColors.Primary)
                ) { Text("Add board") }
            }
        }
    }

    // --- Dialogs ---
    addStatusBoard?.let { board ->
        ColumnEditDialog(
            title = "Add status to ${board.name}",
            confirmLabel = "Add",
            initialName = "",
            initialColor = ColumnColorPalette[board.columns.size % ColumnColorPalette.size],
            onDismiss = { addStatusBoard = null },
            onConfirm = { statusName, color ->
                addStatusBoard = null
                scope.launch {
                    appState.apiClient.createColumn(project.id, board.id, CreateColumnRequest(name = statusName, color = color))
                    onProjectChanged()
                }
            }
        )
    }

    editStatus?.let { (board, column) ->
        ColumnEditDialog(
            title = "Edit status",
            confirmLabel = "Save",
            initialName = column.name,
            initialColor = column.color,
            onDismiss = { editStatus = null },
            onConfirm = { statusName, color ->
                editStatus = null
                scope.launch {
                    appState.apiClient.updateColumn(project.id, board.id, column.id, UpdateColumnRequest(name = statusName, color = color))
                    onProjectChanged()
                }
            }
        )
    }

    deleteStatus?.let { (board, column) ->
        DeleteColumnDialog(
            column = column,
            otherColumns = board.columns.filter { it.id != column.id },
            taskCount = board.tasks.count { it.status == column.id },
            onDismiss = { deleteStatus = null },
            onConfirm = { moveTo ->
                deleteStatus = null
                scope.launch {
                    appState.apiClient.deleteColumn(project.id, board.id, column.id, moveTo)
                    onProjectChanged()
                }
            }
        )
    }

    deleteBoard?.let { board ->
        AlertDialog(
            onDismissRequest = { deleteBoard = null },
            containerColor = NibblerColors.SurfaceContainerHigh,
            shape = RoundedCornerShape(8.dp),
            title = { Text("Delete board \"${board.name}\"?", color = NibblerColors.OnSurface) },
            text = {
                Text(
                    "This permanently deletes the board, its statuses and its ${board.tasks.size} issue(s).",
                    color = NibblerColors.OnSurfaceVariant
                )
            },
            confirmButton = {
                Button(
                    onClick = {
                        deleteBoard = null
                        scope.launch {
                            appState.apiClient.deleteBoard(project.id, board.id)
                            onProjectChanged()
                        }
                    },
                    shape = RoundedCornerShape(8.dp),
                    colors = ButtonDefaults.buttonColors(containerColor = NibblerColors.ErrorContainer, contentColor = NibblerColors.Error)
                ) { Text("Delete board") }
            },
            dismissButton = {
                TextButton(onClick = { deleteBoard = null }) { Text("Cancel", color = NibblerColors.OnSurfaceVariant) }
            }
        )
    }
}

@Composable
private fun SettingsCard(title: String, content: @Composable () -> Unit) {
    Column(
        modifier = Modifier
            .widthIn(max = 720.dp)
            .fillMaxWidth()
            .background(NibblerColors.SurfaceContainer, RoundedCornerShape(8.dp))
            .border(1.dp, NibblerColors.OutlineVariant, RoundedCornerShape(8.dp))
            .padding(16.dp),
        verticalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        Text(title, fontWeight = FontWeight.Bold, fontSize = 15.sp, color = NibblerColors.OnSurface)
        content()
    }
}

/** One board: rename it, delete it and manage its ordered list of statuses. */
@Composable
fun BoardSettingsCard(
    board: BoardDto,
    onRename: (String) -> Unit,
    onDeleteBoard: () -> Unit,
    onAddStatus: () -> Unit,
    onEditStatus: (BoardColumnDto) -> Unit,
    onMoveStatus: (BoardColumnDto, Int) -> Unit,
    onDeleteStatus: (BoardColumnDto) -> Unit
) {
    var boardName by remember(board.id, board.name) { mutableStateOf(board.name) }

    SettingsCard(title = board.name) {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            OutlinedTextField(
                value = boardName,
                onValueChange = { boardName = it },
                label = { Text("Board name") },
                singleLine = true,
                modifier = Modifier.weight(1f)
            )
            OutlinedButton(
                enabled = boardName.isNotBlank() && boardName.trim() != board.name,
                onClick = { onRename(boardName.trim()) },
                shape = RoundedCornerShape(8.dp),
                border = androidx.compose.foundation.BorderStroke(1.dp, NibblerColors.OutlineVariant)
            ) { Text("Rename", color = NibblerColors.OnSurface) }
        }

        Text("Statuses (left to right on the board):", fontSize = 12.sp, color = NibblerColors.OnSurfaceVariant)

        board.columns.forEachIndexed { index, column ->
            val taskCount = board.tasks.count { it.status == column.id }
            // Two lines so a long status name keeps its width on a phone: name and count above, actions below
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .background(NibblerColors.SurfaceContainerLow, RoundedCornerShape(8.dp))
                    .border(1.dp, NibblerColors.OutlineVariant, RoundedCornerShape(8.dp))
                    .padding(horizontal = 10.dp, vertical = 6.dp)
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth().padding(top = 4.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    Box(modifier = Modifier.size(12.dp).background(parseColumnColor(column.color)))
                    Text(column.name, color = NibblerColors.OnSurface, modifier = Modifier.weight(1f))
                    Text("$taskCount issue(s)", fontSize = 11.sp, color = NibblerColors.OnSurfaceVariant)
                }
                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                    TextButton(enabled = index > 0, onClick = { onMoveStatus(column, index - 1) }) { Text("Up", fontSize = 12.sp) }
                    TextButton(enabled = index < board.columns.lastIndex, onClick = { onMoveStatus(column, index + 1) }) { Text("Down", fontSize = 12.sp) }
                    TextButton(onClick = { onEditStatus(column) }) { Text("Edit", fontSize = 12.sp) }
                    TextButton(enabled = board.columns.size > 1, onClick = { onDeleteStatus(column) }) {
                        Text("Delete", fontSize = 12.sp, color = if (board.columns.size > 1) NibblerColors.Error else NibblerColors.OnSurfaceVariant)
                    }
                }
            }
        }

        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            Button(
                onClick = onAddStatus,
                shape = RoundedCornerShape(8.dp),
                colors = ButtonDefaults.buttonColors(containerColor = NibblerColors.Primary)
            ) { Text("+ Add status") }
            Spacer(modifier = Modifier.weight(1f))
            OutlinedButton(
                onClick = onDeleteBoard,
                shape = RoundedCornerShape(8.dp),
                border = androidx.compose.foundation.BorderStroke(1.dp, NibblerColors.OutlineVariant)
            ) { Text("Delete board", color = NibblerColors.Error) }
        }
    }
}

@androidx.compose.ui.tooling.preview.Preview
@Composable
private fun BoardSettingsCardPreview() {
    val columns = listOf(
        BoardColumnDto("c1", "b1", "To do", 0, "#7AA7F7"),
        BoardColumnDto("c2", "b1", "In progress", 1, "#F5B544"),
        BoardColumnDto("c3", "b1", "DONE", 2, "#10B981")
    )
    Column(modifier = Modifier.background(NibblerColors.Background).padding(16.dp)) {
        BoardSettingsCard(
            board = BoardDto(id = "b1", projectId = "p1", name = "Development", columns = columns),
            onRename = {}, onDeleteBoard = {}, onAddStatus = {}, onEditStatus = {}, onMoveStatus = { _, _ -> }, onDeleteStatus = {}
        )
    }
}
